import { useMutation, useQuery } from '@apollo/client';
import * as Tabs from '@radix-ui/react-tabs';
import { useState } from 'react';
import SharedItemRow from '../../components/SharedWithYou/SharedItemRow';
import { SHARED_WITH_YOU_CONSTANTS } from '../../constants/sharedWithMe.constants';
import { MARK_SHARED_STORY_READ } from '../../graphql/resolvers/sharedWithMeMutations/markSharedStoryRead.gql';
import { GET_SHARED_BY_ME } from '../../graphql/resolvers/sharedWithMeQueries/getSharedByMe.gql';
import { GET_SHARED_WITH_ME } from '../../graphql/resolvers/sharedWithMeQueries/getSharedWithMe.gql';
import { bucketSharedItems } from '../../helpers/sharedWithMe.helpers';
import type { SharedItemInterface } from '../../interfaces/sharedWithMe.interface';
import './shared.scss';

const SharedPage = () => {
  const [activeTab, setActiveTab] = useState<'received' | 'sent'>('received');

  const {
    data: receivedData,
    loading: receivedLoading,
    error: receivedError,
    refetch: refetchReceived,
  } = useQuery(GET_SHARED_WITH_ME, {
    variables: { limit: 50 },
    fetchPolicy: 'cache-and-network',
  });

  const {
    data: sentData,
    loading: sentLoading,
    error: sentError,
  } = useQuery(GET_SHARED_BY_ME, {
    variables: { limit: 50 },
    skip: activeTab !== 'sent',
    fetchPolicy: 'cache-and-network',
  });

  const [markRead] = useMutation(MARK_SHARED_STORY_READ);

  const receivedItems: SharedItemInterface[] =
    receivedData?.sharedWithMe?.items || [];
  const sentItems: SharedItemInterface[] = sentData?.sharedByMe || [];

  const buckets = bucketSharedItems(receivedItems);

  const handleRead = async (storyId: string) => {
    try {
      await markRead({ variables: { storyId } });
      refetchReceived();
    } catch (err) {
      console.error('Failed to mark story as read:', err);
    }
  };

  const renderBucket = (label: string, items: SharedItemInterface[]) => {
    if (items.length === 0) return null;
    return (
      <div className="shared-page__bucket">
        <h3 className="shared-page__bucket-label">{label}</h3>
        {items.map((item) => (
          <SharedItemRow
            key={`${item.storyId}-${item.sharedAt}`}
            item={item}
            onRead={handleRead}
          />
        ))}
      </div>
    );
  };

  return (
    <div className="shared-page">
      <Tabs.Root
        defaultValue="received"
        onValueChange={(v) => setActiveTab(v as 'received' | 'sent')}
      >
        <Tabs.List className="shared-page__tabs" aria-label="Shared stories">
          <Tabs.Trigger className="shared-page__tab" value="received">
            {SHARED_WITH_YOU_CONSTANTS.TAB_SHARED_WITH_YOU}
          </Tabs.Trigger>
          <Tabs.Trigger className="shared-page__tab" value="sent">
            {SHARED_WITH_YOU_CONSTANTS.TAB_SHARED_BY_YOU}
          </Tabs.Trigger>
        </Tabs.List>

        <Tabs.Content value="received">
          {receivedLoading && receivedItems.length === 0 && (
            <p className="shared-page__empty">
              {SHARED_WITH_YOU_CONSTANTS.LOADING}
            </p>
          )}

          {receivedError && (
            <p className="shared-page__error">
              {SHARED_WITH_YOU_CONSTANTS.ERROR}
            </p>
          )}

          {!receivedLoading && !receivedError && receivedItems.length === 0 && (
            <p className="shared-page__empty">
              {SHARED_WITH_YOU_CONSTANTS.EMPTY}
            </p>
          )}

          {renderBucket(SHARED_WITH_YOU_CONSTANTS.BUCKET_NEW, buckets.new)}
          {renderBucket(
            SHARED_WITH_YOU_CONSTANTS.BUCKET_THIS_WEEK,
            buckets.thisWeek,
          )}
          {renderBucket(
            SHARED_WITH_YOU_CONSTANTS.BUCKET_LAST_WEEK,
            buckets.lastWeek,
          )}
          {renderBucket(SHARED_WITH_YOU_CONSTANTS.BUCKET_OLDER, buckets.older)}
        </Tabs.Content>

        <Tabs.Content value="sent">
          {sentLoading && sentItems.length === 0 && (
            <p className="shared-page__empty">
              {SHARED_WITH_YOU_CONSTANTS.LOADING}
            </p>
          )}

          {sentError && (
            <p className="shared-page__error">
              {SHARED_WITH_YOU_CONSTANTS.ERROR}
            </p>
          )}

          {!sentLoading && !sentError && sentItems.length === 0 && (
            <p className="shared-page__empty">
              {SHARED_WITH_YOU_CONSTANTS.EMPTY_SHARED_BY_YOU}
            </p>
          )}

          <div className="shared-page__list">
            {sentItems.map((item) => (
              <SharedItemRow
                key={`${item.storyId}-${item.sharedAt}`}
                item={item}
                onRead={() => {}}
              />
            ))}
          </div>
        </Tabs.Content>
      </Tabs.Root>
    </div>
  );
};

export default SharedPage;
